<template>
  <b-modal
    id="incoming-call-modal"
    v-model="callStore.shownIncomingCallModel"
    hide-footer
    centered
    no-close-on-backdrop
    hide-close-icon
    size="md"
    modal-class="incoming-modal"
    header-class="call-header"
  >
    <template #modal-header>
      <div class="header-btn">
        <div @click="callStore.toggleMinimize">
          <MinimizeIcon
            :width="20"
            :height="20"
            class="header-inner-btn cursor-pointer"
          />
        </div>
        <div @click="() => handleDeclineOrEndCall(true)">
          <CloseIcon
            :width="20"
            :height="20"
            class="header-inner-btn cursor-pointer"
          />
        </div>
      </div>
    </template>
    <div class="d-flex flex-column align-items-center call-container">
      <template>
        <img
          v-if="callStore.callerInfo && callStore.callerInfo.avatar"
          :src="callStore.callerInfo.avatar"
          alt="Avatar"
          class="rounded-circle mb-2 pulse-avatar default-avatar"
          :class="{ 'pulse-avatar': !callStore.isEndedCall }"
          width="100"
          height="100"
        />
        <span
          v-else
          class="b-avatar-custom avatar mb-2 default-avatar"
          :class="{ 'pulse-avatar': !callStore.isEndedCall }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40px"
            height="40px"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-user"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </span>
        <h5 v-if="callStore.callerInfo" class="name">
          {{ callStore.callerInfo.name }}
        </h5>
        <h5 v-else class="name">
          {{ $t("alert.unknown") }}
        </h5>
      </template>

      <h5 v-if="callStore.isEndedCall" class="text">
        {{ $t("alert.ended") }}
      </h5>
      <h5 v-else-if="counter > 0" class="text">
        {{ timer }}
      </h5>
      <h5 v-else class="text incoming-text">
        {{ $t("alert.incoming") }}
        <div class="dot-flashing" />
      </h5>

      <div class="end-container" :class="{ invisible: callStore.isEndedCall }">
        <div v-if="callStore.selectedLanguage" class="language">
          <span class="text">
            <flag :iso="getCountryCode(locales.language)" :squared="false" />
            <span class="locale">{{ locales.name }}</span>
          </span>
        </div>

        <div
          class="d-flex w-full btn-container mt-2"
          :class="[
            counter > 0 ? 'justify-content-center' : 'justify-content-between',
          ]"
        >
          <div v-if="counter > 0" class="btn-text">
            <div
              class="btn-action-wrapper"
              :class="{ active: callStore.isMuted }"
              @click="callStore.toggleIsMuted"
            >
              <UnmuteIcon
                v-if="callStore.isMuted"
                class="action-btn cursor-pointer"
              />
              <MuteIcon v-else class="action-btn cursor-pointer" />
            </div>
            <p :class="{ 'active-text': callStore.isMuted }">
              {{ $t("pages.callCenter.callActions.mute") }}
            </p>
          </div>
          <div class="btn-with-text">
            <button
              type="button"
              class="btn-call end-call cursor-pointer"
              :disabled="!callStore.isReleased"
              @click="() => handleDeclineOrEndCall(false)"
            >
              <CallIcon width="32" />
            </button>
            <p>{{ counter > 0 ? $t("alert.end") : $t("alert.decline") }}</p>
          </div>
          <div v-if="counter > 0" class="btn-text">
            <div
              id="popover-button-variant"
              href="#"
              tabindex="0"
              class="btn-action-wrapper"
              :class="{ active: callStore.isSpeaker }"
              @click="callStore.toggleIsSpeaker"
            >
              <SpeakerOnIcon
                v-if="callStore.isSpeaker"
                class="action-btn cursor-pointer"
              />
              <SpeakerIcon v-else class="action-btn cursor-pointer" />
            </div>
            <p :class="{ 'active-text': callStore.isSpeaker }">
              {{ $t("pages.callCenter.callActions.speaker") }}
            </p>
            <b-popover
              :show.sync="callStore.showSpeakerPopover"
              target="popover-button-variant"
              variant="danger"
              triggers="click"
              placement="top"
            >
              <div
                v-for="speaker in callStore.speakers"
                :key="speaker.id"
                class="speakers"
                :class="{
                  'active-text active-bg':
                    callStore.speakerSelectedId === speaker.id,
                }"
                @click="() => callStore.toggleSpeakerOnOff(speaker)"
              >
                {{ speaker.name }}
              </div>
            </b-popover>
          </div>
          <div v-if="counter === 0" class="btn-with-text">
            <button
              type="button"
              class="btn-call cursor-pointer"
              :disabled="!callStore.isReleasedAccept"
              @click="handleAcceptCall"
            >
              <AcceptCallIcon :width="32" />
            </button>
            <p>{{ $t("alert.accept") }}</p>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BPopover } from "bootstrap-vue";
import CallIcon from "@/components/icons/CallIcon.vue";
import MuteIcon from "@/components/icons/MuteIcon.vue";
import MinimizeIcon from "@/components/icons/MinimizeIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import UnmuteIcon from "@/components/icons/UnmuteIcon.vue";
import AcceptCallIcon from "@/components/icons/AcceptCallIcon.vue";
import SpeakerIcon from "@/components/icons/SpeakerIcon.vue";
import SpeakerOnIcon from "@/components/icons/SpeakerOnIcon.vue";
import { useInterval } from "@vueuse/core";
import { computed, watch } from "@vue/composition-api";
import useCallStore from "@/store-pinia/useCallStore";
import languageEn from "@/data/languages/en";
import languageKm from "@/data/languages/km";
import languageZh from "@/data/languages/zh-cn";
import languageVi from "@/data/languages/vi";
import localeCountries from "@/data/localeCountries";

export default {
  components: {
    BModal,
    CallIcon,
    MuteIcon,
    UnmuteIcon,
    MinimizeIcon,
    CloseIcon,
    BPopover,
    AcceptCallIcon,
    SpeakerIcon,
    SpeakerOnIcon,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    locales() {
      let lang = this.languageZh;
      if (this.$i18n.locale === "km") {
        lang = this.languageKm;
      } else if (this.$i18n.locale === "en") {
        lang = this.languageEn;
      } else if (this.$i18n.locale === "vi") {
        lang = this.languageVi;
      }

      const selected = lang.find(
        (l) => l.language === this.callStore.selectedLanguage
      );

      return selected;
    },
  },
  methods: {
    getCountryCode(language) {
      return this.localeCountries.find(
        (e) => e.language.toLowerCase() === language.toLowerCase()
      ).flag;
    },
    handleAcceptCall() {
      this.$emit("call:accept", this.resume);
    },
    handleDeclineOrEndCall(isDisplayConfirmDialog) {
      // If the call ended, just close
      if (this.callStore.isEndedCall) {
        this.callStore.shownIncomingCallModel = false;
        this.callStore.isMinimized = false;
        return;
      }

      // If confirmed pop up, close the modal
      if (isDisplayConfirmDialog) {
        this.$bvModal
          .msgBoxConfirm(
            this.callStore.timer
              ? this.$t("alert.areYouSureToEndThisCall")
              : this.$t("alert.areYouSureToRejectThisCall"),
            {
              title: this.$t("alert.areYouSure"),
              size: "sm",
              buttonSize: "sm",
              okVariant: "primary",
              okTitle: this.callStore.timer
                ? this.$t("alert.endThisCall")
                : this.$t("alert.declineThisCall"),
              cancelTitle: this.$t("button.cancel"),
              cancelVariant: "outline-secondary",
              hideHeaderClose: false,
              centered: true,
            }
          )
          .then((value) => {
            if (value) {
              this.$emit("call:end", this.counter);
            }
          });

        return;
      }

      // Don't close modal
      this.$emit("call:end", this.counter);
    },
  },
  setup() {
    const callStore = useCallStore();
    const { counter, reset, pause, resume } = useInterval(1000, {
      controls: true,
      immediate: false,
    });

    const timer = computed(() => {
      const hours = Math.floor(counter.value / 3600);
      const minutes = Math.floor((counter.value - hours * 3600) / 60);
      const seconds = counter.value - hours * 3600 - minutes * 60;

      let result = `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;

      if (hours > 0) {
        result = `${hours.toString().padStart(2, "0")}:${result}`;
      }

      callStore.timer = result;
      return result;
    });

    watch(
      () => callStore.isEndedCall,
      (newValue) => {
        if (!newValue) {
          pause();
          reset();
        }
      }
    );

    watch(
      () => callStore.shownIncomingCallModel,
      (newValue) => {
        callStore.isReleased = newValue;
      }
    );

    return {
      counter,
      reset,
      pause,
      resume,
      timer,
      callStore,
      languageEn,
      languageKm,
      languageZh,
      languageVi,
      localeCountries,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.call-container {
  padding: 2rem 0;
}

.btn-container {
  gap: 4.5rem;
  width: 50%;
}

.btn-call {
  border: none !important;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50% !important;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  font-size: 30px;
  color: #fff;
  background-color: #5cb85c;
  border-color: #4cae4c;
  box-shadow: none;
  // transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #449d44;
    border-color: #398439;
  }
}

.end-call {
  background-color: #d9534f;
  border-color: #d43f3a;
  transform: unset;
  &:hover {
    background-color: #c9302c;
    border-color: #ac2925;
  }
}

.avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  font-size: 30px;
  padding: 0.5rem;
  border-radius: 50%;
  height: 100px;
  width: 100px;
}

.action-btn {
  border-radius: 50%;
  height: 2rem;
  width: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-action-wrapper {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  border: 1px solid rgb(206, 206, 206);
  border-radius: 50%;
  width: 4rem;
  height: 4rem;
  &:hover {
    background-color: #eee;
  }
}

.btn-action-wrapper.active {
  border: 1px solid #8d5185;
}

.btn-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  // margin-top: 1rem;
}

.header-btn {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  padding: 1rem 0;
}

.text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
}

.name {
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
  letter-spacing: 0em;
  text-align: center;
}

.language {
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  height: 40px;
  padding-left: 1rem;
  padding-right: 1rem;
  border-radius: 10px;
  box-shadow: 0px 0px 3px 0px #00000040;
}

.btn-with-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0px;
    text-align: center;
  }
}

.default-avatar {
  border: 2px solid #8d5185;
}

.active-text {
  color: #8d5185;
}

.incoming-modal .modal-content {
  border-radius: 20px !important;
}

.call-header {
  border-radius: 20px !important;
  background-color: transparent !important;
}

.cursor-pointer {
  cursor: pointer;
}

.pulse-avatar {
  background: black;
  border-radius: 50%;

  box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
  transform: scale(1);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 #8d518526;
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px #8d518526;
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 #8d518526;
  }
}

.dot-flashing {
  position: relative;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: #8d5185;
  color: #8d5185;
  animation: dot-flashing 1s infinite linear alternate;
  animation-delay: 0.5s;
  transform: scale(0.3);
  margin-bottom: 1.5px;
}

.dot-flashing::before,
.dot-flashing::after {
  content: "";
  display: inline-block;
  position: absolute;
  top: 0;
}

.dot-flashing::before {
  left: -15px;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: #8d5185;
  color: #8d5185;
  animation: dot-flashing 1s infinite alternate;
  animation-delay: 0s;
}

.dot-flashing::after {
  left: 15px;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: #8d5185;
  color: #8d5185;
  animation: dot-flashing 1s infinite alternate;
  animation-delay: 1s;
}

@keyframes dot-flashing {
  0% {
    background-color: #8d5185;
  }
  50%,
  100% {
    background-color: rgba(141, 81, 133, 0.2);
  }
}

.incoming-text {
  display: flex;
  align-items: end;
  gap: 0.25rem;
}

.end-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.invisible {
  visibility: hidden;
}

.speakers {
  font-size: 12px;
  padding: 0.5rem 1rem;
  color: black;
  cursor: pointer;

  &:hover {
    background-color: #eee;
  }
}

.active-bg {
  background-color: #eee;
}

.locale {
  margin-left: 0.5rem;
}

button:disabled,
button[disabled] {
  background-color: grey !important;
  cursor: default;
}
</style>
